import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import axios from 'axios';
import { 
  Plug, 
  Save,
  RefreshCw,
  Server,
  Wifi,
  Plus,
  Trash2,
  Eye,
  EyeOff,
  Star,
  Zap,
  Monitor,
  Apple,
  Download,
  Upload,
  FileUp,
  Check,
  X
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

import { API_BASE_URL } from '../utils/api';
import StarCodeTemplateManager from '../components/StarCodeTemplateManager';

const API_URL = API_BASE_URL;

export default function AdminIntegrations() {
  const { admin } = useOutletContext();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sipDomains, setSipDomains] = useState([]);
  const [providerSettings, setProviderSettings] = useState({});
  const [showPasswords, setShowPasswords] = useState({});
  const [activeTab, setActiveTab] = useState('hero');
  const [desktopVersions, setDesktopVersions] = useState({});
  const [savingDesktop, setSavingDesktop] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploading, setUploading] = useState({});

  useEffect(() => {
    fetchIntegrations();
    fetchDesktopVersions();
    fetchUploadedFiles();
  }, []);

  const fetchIntegrations = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API_URL}/api/admin-portal/integrations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSipDomains(response.data.sip_domains || []);
      setProviderSettings(response.data.provider_settings || {});
    } catch (error) {
      toast.error('Failed to load integrations');
    } finally {
      setLoading(false);
    }
  };

  const fetchDesktopVersions = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/desktop/versions`);
      const versions = {};
      (response.data.versions || []).forEach(v => {
        versions[v.platform] = v;
      });
      setDesktopVersions(versions);
    } catch (error) {
      console.error('Failed to load desktop versions:', error);
    }
  };

  const fetchUploadedFiles = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/desktop/files`);
      setUploadedFiles(response.data.files || []);
    } catch (error) {
      console.error('Failed to load uploaded files:', error);
    }
  };

  const handleFileUpload = async (platform, file) => {
    if (!file) return;
    
    setUploading(prev => ({ ...prev, [platform]: true }));
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const token = localStorage.getItem('admin_token');
      const response = await axios.post(
        `${API_URL}/api/desktop/upload/${platform}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            console.log(`Upload progress: ${percent}%`);
          }
        }
      );
      
      toast.success(`Uploaded ${response.data.original_filename || response.data.filename} (${response.data.size_mb}MB)`);
      
      // Auto-fill the download URL and original filename from the API response
      setDesktopVersions(prev => ({
        ...prev,
        [platform]: {
          ...prev[platform],
          download_url: response.data.download_url,
          original_filename: response.data.original_filename,
          file_size_mb: response.data.size_mb
        }
      }));
      
      fetchUploadedFiles();
    } catch (error) {
      const message = error.response?.data?.detail || 'Upload failed';
      toast.error(message);
    } finally {
      setUploading(prev => ({ ...prev, [platform]: false }));
    }
  };

  const deleteFile = async (platform) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API_URL}/api/desktop/file/${platform}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Deleted ${platform} installer`);
      fetchUploadedFiles();
      fetchDesktopVersions();
    } catch (error) {
      toast.error('Failed to delete file');
    }
  };

  const saveDesktopVersion = async (platform) => {
    setSavingDesktop(true);
    try {
      const token = localStorage.getItem('admin_token');
      const data = desktopVersions[platform];
      await axios.put(`${API_URL}/api/desktop/version/${platform}`, {
        version: data.version,
        download_url: data.download_url,
        release_notes: data.release_notes,
        min_required_version: data.min_required_version || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${platform.charAt(0).toUpperCase() + platform.slice(1)} version updated`);
      fetchDesktopVersions();
    } catch (error) {
      toast.error(`Failed to update ${platform} version`);
    } finally {
      setSavingDesktop(false);
    }
  };

  const updateDesktopVersion = (platform, field, value) => {
    setDesktopVersions(prev => ({
      ...prev,
      [platform]: {
        ...prev[platform],
        [field]: value
      }
    }));
  };

  const updateSipDomain = async (domainId, updates) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(`${API_URL}/api/admin-portal/integrations/sip-domain/${domainId}`, updates, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('SIP domain updated');
      fetchIntegrations();
    } catch (error) {
      toast.error('Failed to update SIP domain');
    }
  };

  const saveProviderSettings = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(`${API_URL}/api/admin-portal/integrations/provider-settings`, providerSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Provider settings saved');
    } catch (error) {
      toast.error('Failed to save provider settings');
    } finally {
      setSaving(false);
    }
  };

  const togglePasswordVisibility = (id) => {
    setShowPasswords(prev => ({ ...prev, [id]: !prev[id] }));
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[400px]">
        <div className="animate-spin w-8 h-8 border-2 border-red-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white">Integrations</h1>
          <p className="text-slate-400 mt-1">
            Configure SIP domains, provider settings, and star code templates
          </p>
        </div>
        <Button variant="outline" onClick={fetchIntegrations} className="border-slate-600 text-slate-300 hover:bg-slate-700">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-800/50 border-slate-700 mb-6">
          <TabsTrigger value="hero" className="data-[state=active]:bg-red-500 data-[state=active]:text-white">
            <Zap className="w-4 h-4 mr-2" />
            Hero
          </TabsTrigger>
          <TabsTrigger value="sip" className="data-[state=active]:bg-red-500 data-[state=active]:text-white">
            <Server className="w-4 h-4 mr-2" />
            SIP Domains
          </TabsTrigger>
          <TabsTrigger value="desktop" className="data-[state=active]:bg-red-500 data-[state=active]:text-white">
            <Monitor className="w-4 h-4 mr-2" />
            Desktop App
          </TabsTrigger>
          <TabsTrigger value="global" className="data-[state=active]:bg-red-500 data-[state=active]:text-white">
            <Plug className="w-4 h-4 mr-2" />
            Global Settings
          </TabsTrigger>
        </TabsList>

        {/* Hero Tab */}
        <TabsContent value="hero" className="space-y-6">
          {/* Hero Provider Settings */}
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Zap className="w-5 h-5 text-amber-400" />
                Hero Cloud PBX Settings
              </CardTitle>
              <CardDescription className="text-slate-400">
                Configure the Hero Cloud PBX integration
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label className="text-slate-300">Hero API URL</Label>
                  <Input
                    className="bg-slate-900/50 border-slate-600 text-white font-mono text-sm"
                    placeholder="https://api.hero.co.nz"
                    value={providerSettings.hero_api_url || ''}
                    onChange={(e) => setProviderSettings({ ...providerSettings, hero_api_url: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Hero Portal URL</Label>
                  <Input
                    className="bg-slate-900/50 border-slate-600 text-white font-mono text-sm"
                    placeholder="https://portal1.connect-us.co.nz"
                    value={providerSettings.hero_portal_url || ''}
                    onChange={(e) => setProviderSettings({ ...providerSettings, hero_portal_url: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Admin Portal URL</Label>
                  <Input
                    className="bg-slate-900/50 border-slate-600 text-white font-mono text-sm"
                    placeholder="https://admin1.connect-us.co.nz"
                    value={providerSettings.hero_admin_url || ''}
                    onChange={(e) => setProviderSettings({ ...providerSettings, hero_admin_url: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Default SIP Domain</Label>
                  <Input
                    className="bg-slate-900/50 border-slate-600 text-white font-mono text-sm"
                    placeholder="securevoip.nz"
                    value={providerSettings.hero_sip_domain || ''}
                    onChange={(e) => setProviderSettings({ ...providerSettings, hero_sip_domain: e.target.value })}
                  />
                </div>
              </div>
              
              <div className="flex justify-end pt-4">
                <Button onClick={saveProviderSettings} disabled={saving} className="bg-red-500 hover:bg-red-600">
                  {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Hero Settings
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Star Code Templates for Hero */}
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Star className="w-5 h-5 text-amber-400" />
                Star Code Templates
              </CardTitle>
              <CardDescription className="text-slate-400">
                Configure default star codes for Hero Cloud PBX. These codes will be applied when a new company initializes their star codes.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <StarCodeTemplateManager provider="hero" />
            </CardContent>
          </Card>
        </TabsContent>

        {/* SIP Domains Tab */}
        <TabsContent value="sip">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Server className="w-5 h-5 text-blue-400" />
                SIP Domains
              </CardTitle>
              <CardDescription className="text-slate-400">
                Configure SIP server connections for WebRTC calls
              </CardDescription>
            </CardHeader>
            <CardContent>
              {sipDomains.length > 0 ? (
                <div className="space-y-4">
                  {sipDomains.map(domain => (
                    <div key={domain.id} className="p-4 rounded-lg bg-slate-700/30">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h4 className="font-medium text-white">{domain.name || domain.id}</h4>
                          <p className="text-sm text-slate-400">{domain.sip_domain}</p>
                        </div>
                        <Wifi className="w-5 h-5 text-green-400" />
                      </div>
                      <div className="grid gap-4 sm:grid-cols-2">
                        <div className="space-y-2">
                          <Label className="text-slate-300">WSS URL</Label>
                          <Input
                            className="bg-slate-900/50 border-slate-600 text-white font-mono text-sm"
                            value={domain.wss_url || ''}
                            onChange={(e) => {
                              const updated = sipDomains.map(d => 
                                d.id === domain.id ? { ...d, wss_url: e.target.value } : d
                              );
                              setSipDomains(updated);
                            }}
                            onBlur={() => updateSipDomain(domain.id, { wss_url: domain.wss_url })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-slate-300">STUN Server</Label>
                          <Input
                            className="bg-slate-900/50 border-slate-600 text-white font-mono text-sm"
                            value={domain.stun_server || ''}
                            onChange={(e) => {
                              const updated = sipDomains.map(d => 
                                d.id === domain.id ? { ...d, stun_server: e.target.value } : d
                              );
                              setSipDomains(updated);
                            }}
                            onBlur={() => updateSipDomain(domain.id, { stun_server: domain.stun_server })}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Server className="w-12 h-12 mx-auto text-slate-600 mb-3" />
                  <p className="text-slate-400">No SIP domains configured</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Desktop App Tab */}
        <TabsContent value="desktop" className="space-y-6">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Monitor className="w-5 h-5 text-blue-400" />
                Desktop App Version Management
              </CardTitle>
              <CardDescription className="text-slate-400">
                Manage version information for Windows and Mac desktop applications. 
                Users will see an update prompt when a new version is available.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Windows Version */}
              <div className="p-4 bg-slate-900/50 rounded-lg border border-slate-700">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Monitor className="w-5 h-5 text-blue-400" />
                    <h3 className="text-lg font-semibold text-white">Windows</h3>
                    {desktopVersions.windows?.version && (
                      <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded text-sm">
                        v{desktopVersions.windows.version}
                      </span>
                    )}
                  </div>
                  {/* Upload Button */}
                  <div className="flex items-center gap-2">
                    <input
                      type="file"
                      id="windows-upload"
                      accept=".exe"
                      className="hidden"
                      onChange={(e) => handleFileUpload('windows', e.target.files[0])}
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-blue-500/50 text-blue-400 hover:bg-blue-500/20"
                      onClick={() => document.getElementById('windows-upload').click()}
                      disabled={uploading.windows}
                    >
                      {uploading.windows ? (
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Upload className="w-4 h-4 mr-2" />
                      )}
                      {uploading.windows ? 'Uploading...' : 'Upload .exe'}
                    </Button>
                    {uploadedFiles.find(f => f.platform === 'windows') && (
                      <span className="flex items-center gap-1 text-xs text-green-400">
                        <Check className="w-3 h-3" />
                        {desktopVersions.windows?.original_filename || uploadedFiles.find(f => f.platform === 'windows')?.filename} ({desktopVersions.windows?.file_size_mb || uploadedFiles.find(f => f.platform === 'windows')?.size_mb}MB)
                      </span>
                    )}
                  </div>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label className="text-slate-300">Current Version</Label>
                    <Input
                      className="bg-slate-800 border-slate-600 text-white font-mono"
                      placeholder="1.0.1"
                      value={desktopVersions.windows?.version || ''}
                      onChange={(e) => updateDesktopVersion('windows', 'version', e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Download URL</Label>
                    <Input
                      className="bg-slate-800 border-slate-600 text-white font-mono text-sm"
                      placeholder="https://connect-us.co.nz/downloads/CONNECT-Setup.exe"
                      value={desktopVersions.windows?.download_url || ''}
                      onChange={(e) => updateDesktopVersion('windows', 'download_url', e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Min Required Version (Optional)</Label>
                    <Input
                      className="bg-slate-800 border-slate-600 text-white font-mono"
                      placeholder="1.0.0"
                      value={desktopVersions.windows?.min_required_version || ''}
                      onChange={(e) => updateDesktopVersion('windows', 'min_required_version', e.target.value)}
                    />
                    <p className="text-xs text-slate-500">Force update if user's version is below this</p>
                  </div>
                  <div className="space-y-2 sm:col-span-2">
                    <Label className="text-slate-300">Release Notes</Label>
                    <Textarea
                      className="bg-slate-800 border-slate-600 text-white"
                      placeholder="What's new in this version..."
                      rows={2}
                      value={desktopVersions.windows?.release_notes || ''}
                      onChange={(e) => updateDesktopVersion('windows', 'release_notes', e.target.value)}
                    />
                  </div>
                </div>
                <div className="flex justify-end mt-4">
                  <Button 
                    onClick={() => saveDesktopVersion('windows')} 
                    disabled={savingDesktop}
                    className="bg-blue-500 hover:bg-blue-600"
                  >
                    {savingDesktop ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Save Windows Version
                  </Button>
                </div>
              </div>

              {/* Mac Version */}
              <div className="p-4 bg-slate-900/50 rounded-lg border border-slate-700">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Apple className="w-5 h-5 text-slate-300" />
                    <h3 className="text-lg font-semibold text-white">macOS</h3>
                    {desktopVersions.mac?.version && (
                      <span className="px-2 py-0.5 bg-slate-500/20 text-slate-400 rounded text-sm">
                        v{desktopVersions.mac.version}
                      </span>
                    )}
                  </div>
                  {/* Upload Button */}
                  <div className="flex items-center gap-2">
                    <input
                      type="file"
                      id="mac-upload"
                      accept=".dmg"
                      className="hidden"
                      onChange={(e) => handleFileUpload('mac', e.target.files[0])}
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-slate-500/50 text-slate-400 hover:bg-slate-500/20"
                      onClick={() => document.getElementById('mac-upload').click()}
                      disabled={uploading.mac}
                    >
                      {uploading.mac ? (
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Upload className="w-4 h-4 mr-2" />
                      )}
                      {uploading.mac ? 'Uploading...' : 'Upload .dmg'}
                    </Button>
                    {uploadedFiles.find(f => f.platform === 'mac') && (
                      <span className="flex items-center gap-1 text-xs text-green-400">
                        <Check className="w-3 h-3" />
                        {desktopVersions.mac?.original_filename || uploadedFiles.find(f => f.platform === 'mac')?.filename} ({desktopVersions.mac?.file_size_mb || uploadedFiles.find(f => f.platform === 'mac')?.size_mb}MB)
                      </span>
                    )}
                  </div>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label className="text-slate-300">Current Version</Label>
                    <Input
                      className="bg-slate-800 border-slate-600 text-white font-mono"
                      placeholder="1.0.0"
                      value={desktopVersions.mac?.version || ''}
                      onChange={(e) => updateDesktopVersion('mac', 'version', e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Download URL</Label>
                    <Input
                      className="bg-slate-800 border-slate-600 text-white font-mono text-sm"
                      placeholder="https://connect-us.co.nz/downloads/CONNECT.dmg"
                      value={desktopVersions.mac?.download_url || ''}
                      onChange={(e) => updateDesktopVersion('mac', 'download_url', e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Min Required Version (Optional)</Label>
                    <Input
                      className="bg-slate-800 border-slate-600 text-white font-mono"
                      placeholder="1.0.0"
                      value={desktopVersions.mac?.min_required_version || ''}
                      onChange={(e) => updateDesktopVersion('mac', 'min_required_version', e.target.value)}
                    />
                    <p className="text-xs text-slate-500">Force update if user's version is below this</p>
                  </div>
                  <div className="space-y-2 sm:col-span-2">
                    <Label className="text-slate-300">Release Notes</Label>
                    <Textarea
                      className="bg-slate-800 border-slate-600 text-white"
                      placeholder="What's new in this version..."
                      rows={2}
                      value={desktopVersions.mac?.release_notes || ''}
                      onChange={(e) => updateDesktopVersion('mac', 'release_notes', e.target.value)}
                    />
                  </div>
                </div>
                <div className="flex justify-end mt-4">
                  <Button 
                    onClick={() => saveDesktopVersion('mac')} 
                    disabled={savingDesktop}
                    className="bg-slate-600 hover:bg-slate-500"
                  >
                    {savingDesktop ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Save macOS Version
                  </Button>
                </div>
              </div>

              {/* Info Box */}
              <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                <div className="flex gap-3">
                  <Download className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-slate-300">
                    <p className="font-medium text-blue-400 mb-1">How version updates work:</p>
                    <ul className="list-disc list-inside space-y-1 text-slate-400">
                      <li>Desktop app checks for updates on startup</li>
                      <li>If a newer version exists, users see an "Update Available" prompt</li>
                      <li>If their version is below "Min Required", they must update to continue</li>
                      <li>Upload new installers to your website and update the URL here</li>
                    </ul>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Global Settings Tab */}
        <TabsContent value="global">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Plug className="w-5 h-5 text-amber-400" />
                Global Provider Settings
              </CardTitle>
              <CardDescription className="text-slate-400">
                Global settings for the CONNECT platform
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label className="text-slate-300">TURN Server URL</Label>
                  <Input
                    className="bg-slate-900/50 border-slate-600 text-white font-mono text-sm"
                    placeholder="turn:turn.example.com:3478"
                    value={providerSettings.turn_url || ''}
                    onChange={(e) => setProviderSettings({ ...providerSettings, turn_url: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">TURN Username</Label>
                  <Input
                    className="bg-slate-900/50 border-slate-600 text-white"
                    value={providerSettings.turn_username || ''}
                    onChange={(e) => setProviderSettings({ ...providerSettings, turn_username: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">TURN Password</Label>
                  <div className="relative">
                    <Input
                      type={showPasswords.turn ? 'text' : 'password'}
                      className="bg-slate-900/50 border-slate-600 text-white pr-10"
                      value={providerSettings.turn_password || ''}
                      onChange={(e) => setProviderSettings({ ...providerSettings, turn_password: e.target.value })}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-1 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                      onClick={() => togglePasswordVisibility('turn')}
                    >
                      {showPasswords.turn ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Webhook Base URL</Label>
                  <Input
                    className="bg-slate-900/50 border-slate-600 text-white font-mono text-sm"
                    placeholder="https://connect.example.com"
                    value={providerSettings.webhook_base_url || ''}
                    onChange={(e) => setProviderSettings({ ...providerSettings, webhook_base_url: e.target.value })}
                  />
                </div>
              </div>
              
              <div className="flex justify-end pt-4">
                <Button onClick={saveProviderSettings} disabled={saving} className="bg-red-500 hover:bg-red-600">
                  {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Global Settings
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
